import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { NotificationList, DrugScreenNotification } from '../components/NotificationSystem';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import EmergencyButton from '../components/EmergencyButton';
import { authFetch } from '../lib/authFetch';
import PageHeader from '../components/PageHeader';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function NotificationsPage() {
  useTheme4A();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [activeDrugScreen, setActiveDrugScreen] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    fetchActiveDrugScreen();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveDrugScreen = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/drug-screens/active`);
      if (response.ok) {
        const data = await response.json();
        setActiveDrugScreen(data);
      }
    } catch (error) {
      console.error('Failed to fetch drug screen:', error);
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
              });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      // Mark all as read
      await Promise.all(
        notifications.filter(n => !n.read).map(n => 
          authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${n.notification_id}/read`, {
            method: 'PATCH',
                      })
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 pb-20">
      <PageHeader title="Notifications" />

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {t('dashboard.notifications')}
              </h1>
              <p className="text-sm text-blue-100">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                onClick={handleMarkAllRead}
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="mark-all-read-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Active Drug Screen - Always show at top if exists */}
          {activeDrugScreen && (
            <div className="mb-6">
              <DrugScreenNotification
                deadline={activeDrugScreen.deadline}
                clientId={activeDrugScreen.client_id}
                labs={['QML Pathology', 'Sullivan Nicolaides', 'Dorevitch Pathology']}
                onUploadResults={() => navigate('/drug-screen')}
              />
            </div>
          )}

          {/* Notifications List */}
          <div className="bg-white rounded-xl p-4" data-testid="notifications-list">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 && !activeDrugScreen ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">No notifications</h3>
                <p className="text-sm text-gray-500">You're all caught up!</p>
              </div>
            ) : (
              <NotificationList
                notifications={notifications.map(n => ({
                  id: n.notification_id,
                  type: n.urgent ? 'urgent' : n.type || 'info',
                  title: n.title,
                  message: n.message,
                  timestamp: new Date(n.created_at).toLocaleDateString(),
                  read: n.read
                }))}
                onDismiss={handleDismiss}
                onAction={(notification) => {
                  if (notification.route) {
                    navigate(notification.route);
                  }
                }}
              />
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default NotificationsPage;
